import { ReactNode, useMemo, useState } from "react";
import { FolderPlus, Plus, Search } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Separator } from "~/components/ui/separator";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useFavorites } from "~/hooks/useFavorites";
import { useFetcher } from "~/hooks/useFetcher";
import { useSort, type SortOption } from "~/hooks/useSort";
import { useViewMode } from "~/hooks/useViewMode";
import { NEXTGEN_MAX_CONTENT_WIDTH } from "~/lib/layout";
import { cn } from "~/lib/utils";
import { DividedList } from "~/pages/manifold-nextgen/components/DividedList";
import { ListLoadError } from "~/pages/manifold-nextgen/components/ListLoadError";
import { ListLoadingSkeleton } from "~/pages/manifold-nextgen/components/ListLoadingSkeleton";
import { SortMenu } from "~/pages/manifold-nextgen/components/SortMenu";
import { ViewToggle } from "~/pages/manifold-nextgen/components/ViewToggle";
import { useProjectCollaboratorsBatch } from "~/pages/manifold-nextgen/hooks/useProjectShare";
import {
  ProjectList as ProjectListType,
  ProjectListItem,
  projectListPath,
} from "~/services/projects";
import { NewProjectDialog } from "./NewProjectDialog";
import { ProjectCard } from "./ProjectCard";

const PROJECT_SORT_OPTIONS: SortOption<ProjectListItem>[] = [
  {
    key: "last_activity_at",
    label: "Last activity",
    kind: "date",
    accessor: (p) => Date.parse(p.last_activity_at),
    defaultDirection: "desc",
  },
  {
    key: "name",
    label: "Name",
    kind: "alpha",
    accessor: (p) => p.name,
    defaultDirection: "asc",
  },
];

export const Projects = () => {
  const { data, error, isLoading } = useFetcher<ProjectListType>(projectListPath());
  // Second phase: load collaborators for the page once the list resolves, so
  // cards paint immediately and a collaborators hiccup only blanks the avatars.
  const projectIds = useMemo(() => (data?.items ?? []).map((p) => p.id), [data]);
  const collaboratorsByProject = useProjectCollaboratorsBatch(projectIds);
  const collaboratorsFor = (id: string) =>
    collaboratorsByProject ? (collaboratorsByProject[id] ?? []) : undefined;
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [viewMode, setViewMode] = useViewMode("nextgen-view-mode");

  const headerActions = useMemo(
    () =>
      [
        <Button key="new-project" size="sm" className="text-xs" onClick={() => setCreateOpen(true)}>
          <Plus aria-hidden="true" />
          New Project
        </Button>,
      ] as [ReactNode],
    []
  );

  useDocumentTitle("Projects");
  useNextGenHeader({ actions: headerActions });

  const filtered = (() => {
    if (!data?.items) {
      return [];
    }
    if (!search.trim()) {
      return data.items;
    }
    const lower = search.toLowerCase();
    return data.items.filter((p) => p.name.toLowerCase().includes(lower));
  })();

  const { sortKey, direction, activeOption, setSortKey, toggleDirection, sorted } = useSort(
    "nextgen-project-sort",
    PROJECT_SORT_OPTIONS,
    filtered
  );

  const { isFavorite, toggleFavorite, favorites, others } = useFavorites(
    "project-favorites",
    sorted,
    (p) => p.id
  );

  // While searching, the Favorites/All split is noise — show a single flat list.
  const showGroups = !search.trim() && favorites.length > 0;

  const renderProjects = (items: ProjectListItem[]) =>
    viewMode === "card" ? (
      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" role="list">
        {items.map((project) => (
          <li key={project.id} className="flex flex-col">
            <ProjectCard
              project={project}
              collaborators={collaboratorsFor(project.id)}
              view="card"
              favorite={isFavorite(project.id)}
              onToggleFavorite={() => toggleFavorite(project.id)}
            />
          </li>
        ))}
      </ul>
    ) : (
      <DividedList
        items={items}
        renderItem={(project) => (
          <ProjectCard
            project={project}
            collaborators={collaboratorsFor(project.id)}
            view="list"
            favorite={isFavorite(project.id)}
            onToggleFavorite={() => toggleFavorite(project.id)}
          />
        )}
      />
    );

  return (
    <>
      <div
        className="mx-auto flex w-full flex-col gap-6 px-4 py-4"
        style={{ maxWidth: NEXTGEN_MAX_CONTENT_WIDTH }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="relative w-full max-w-lg">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <input
              type="search"
              placeholder="Search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search projects"
              className={cn(
                "h-8 w-full rounded-md border border-input bg-transparent pl-9 pr-3 py-1",
                "text-sm placeholder:text-muted-foreground shadow-xs",
                "focus-visible:outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
                "transition-[color,box-shadow]"
              )}
            />
          </div>
          <div className="flex items-center gap-2">
            <SortMenu
              options={PROJECT_SORT_OPTIONS}
              sortKey={sortKey}
              direction={direction}
              activeLabel={activeOption.label}
              onSelectKey={setSortKey}
              onToggleDirection={toggleDirection}
            />
            <ViewToggle value={viewMode} onChange={setViewMode} />
          </div>
        </div>

        {isLoading && viewMode === "list" && (
          <ListLoadingSkeleton
            aria-label="Loading projects"
            columns={[{ width: "w-36", height: "h-6", rounded: true }, { width: "w-36" }]}
          />
        )}
        {isLoading && viewMode === "card" && (
          <div
            role="status"
            aria-label="Loading projects"
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-20 animate-pulse rounded-xl border border-border bg-muted"
              />
            ))}
          </div>
        )}

        {!isLoading && error && (
          <ListLoadError error={error} resourceLabel="projects" className="py-16" />
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <FolderPlus className="size-8 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm font-medium text-foreground">
              {search ? "No projects match your search" : "No projects yet"}
            </p>
            {!search && (
              <p className="text-xs text-muted-foreground">Create one to group your work.</p>
            )}
          </div>
        )}

        {!isLoading &&
          !error &&
          sorted.length > 0 &&
          (showGroups ? (
            <>
              <section className="flex flex-col gap-3">
                <h2 className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Favorites
                </h2>
                {renderProjects(favorites)}
              </section>
              {others.length > 0 && (
                <>
                  <Separator />
                  {renderProjects(others)}
                </>
              )}
            </>
          ) : (
            renderProjects(sorted)
          ))}
      </div>

      <NewProjectDialog open={createOpen} onOpenChange={setCreateOpen} />
    </>
  );
};
